import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Robot, Plus, Trash, PaperPlaneTilt, ChatCircleText, Copy, User } from "@phosphor-icons/react";
import { relTime } from "../lib/labels";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function IaNetkom() {
  const [sessions, setSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [quotaStatus, setQuotaStatus] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const loadSessions = async () => {
    try {
      const r = await api.get("/ia-netkom/sessions");
      setSessions(r.data);
    } catch { /* noop */ }
  };
  const loadSuggestions = async () => {
    try { const r = await api.get("/ia-netkom/suggestions"); setSuggestions(r.data); } catch { /* noop */ }
  };
  const loadStatus = async () => {
    try {
      const r = await api.get("/ia-netkom/status");
      setDisabled(r.data?.enabled === false);
      setQuotaStatus(r.data);
    } catch { /* noop */ }
  };

  useEffect(() => { loadStatus(); loadSessions(); loadSuggestions(); }, []);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);
  useEffect(() => {
    if (!streaming) { setElapsed(0); return; }
    setElapsed(0);
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [streaming]);

  const openSession = async (s) => {
    try {
      const r = await api.get(`/ia-netkom/sessions/${s.id}`);
      setCurrent(r.data);
      setMessages(r.data.messages || []);
    } catch { toast.error("Impossible d'ouvrir la discussion"); }
  };

  const newSession = () => {
    setCurrent(null); setMessages([]); setInput("");
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const deleteSession = async (s, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Supprimer la discussion "${s.title}" ?`)) return;
    try {
      await api.delete(`/ia-netkom/sessions/${s.id}`);
      if (current?.id === s.id) newSession();
      loadSessions();
      toast.success("Discussion supprimée");
    } catch { toast.error("Suppression impossible"); }
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming || disabled) return;
    setInput("");
    const userMsg = { role: "user", content: text, at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "", at: new Date().toISOString(), streaming: true }]);
    setStreaming(true);
    try {
      // Standard JSON POST (no SSE) — production mobile proxies drop long-lived streams.
      const { data } = await api.post("/ia-netkom/chat", {
        session_id: current?.id,
        message: text,
      }, { timeout: 120000 });
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: data.reply || "", at: userMsg.at };
        return copy;
      });
      if (data.session_id && !current) {
        setCurrent({ id: data.session_id });
      }
      loadSessions();
      loadStatus();  // refresh quota counters after each successful message
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message;
      if (status === 401) {
        // Axios interceptor already handles redirect; just clean the placeholder.
        setMessages((m) => m.filter((x) => !x.streaming));
        return;
      }
      toast.error(`Erreur : ${detail}`);
      setMessages((m) => m.filter((x) => !x.streaming));
    } finally {
      setStreaming(false);
    }
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content).then(() => toast.success("Copié"));
  };

  if (disabled) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-2xl bg-muted flex items-center justify-center">
          <Robot size={32} className="text-muted-foreground"/>
        </div>
        <h1 className="font-display text-2xl font-bold">Assistant IA désactivé</h1>
        <p className="text-sm text-muted-foreground">L&apos;administrateur a désactivé temporairement l&apos;assistant IA. Contactez-le pour l&apos;activer.</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-2rem)] gap-4 -mt-4 -mx-4 md:mx-0 md:mt-0">
      <aside className="hidden md:flex flex-col w-72 shrink-0 border-r bg-muted/20">
        <div className="p-3 border-b">
          <Button className="w-full" onClick={newSession} data-testid="new-ia-session">
            <Plus size={16} className="mr-2"/> Nouvelle discussion
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 && (
            <p className="text-xs text-muted-foreground px-2 py-4 text-center">Aucune discussion pour l&apos;instant.</p>
          )}
          {sessions.map((s) => (
            <button key={s.id} data-testid={`ia-session-${s.id}`} onClick={() => openSession(s)}
              className={`w-full text-left group p-2 rounded-md hover:bg-muted transition-colors ${current?.id === s.id ? "bg-muted" : ""}`}>
              <div className="flex items-start gap-2">
                <ChatCircleText size={14} className="mt-1 shrink-0 text-muted-foreground"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-[0.68rem] text-muted-foreground">{relTime(s.updated_at || s.created_at)} · {s.message_count} msg</div>
                </div>
                <button onClick={(e) => deleteSession(s, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive shrink-0"
                  title="Supprimer" data-testid={`ia-session-delete-${s.id}`}>
                  <Trash size={12}/>
                </button>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <main className="flex-1 flex flex-col min-w-0">
        <header className="p-4 border-b bg-background flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <Robot size={22} weight="fill" className="text-white"/>
            </div>
            <div>
              <h1 className="font-display text-lg font-bold leading-tight">Assistant IA</h1>
              <p className="text-xs text-muted-foreground">
                {quotaStatus?.on_fallback
                  ? "Mode économique · Gemini Flash (plafond atteint)"
                  : (quotaStatus?.primary_model_label || "Assistant IA généraliste")}
              </p>
            </div>
          </div>
          {quotaStatus && !quotaStatus.admin_exempt && (
            <div className="text-right text-xs" data-testid="ia-quota-status">
              {quotaStatus.on_fallback && (
                <div className="text-amber-600 dark:text-amber-400 font-semibold mb-1">⚡ Mode économique actif</div>
              )}
              {quotaStatus.limits?.daily_msg > 0 && (
                <div className={quotaStatus.usage.daily_msg >= quotaStatus.limits.daily_msg ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-muted-foreground"}>
                  📅 {quotaStatus.usage.daily_msg} / {quotaStatus.limits.daily_msg} msg aujourd&apos;hui
                </div>
              )}
              {quotaStatus.limits?.monthly_cost > 0 && (
                <div className={quotaStatus.usage.monthly_cost >= quotaStatus.limits.monthly_cost ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-muted-foreground"}>
                  💰 {quotaStatus.usage.monthly_cost.toFixed(2)} / {quotaStatus.limits.monthly_cost.toFixed(2)} $ ce mois
                </div>
              )}
            </div>
          )}
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6 space-y-6" data-testid="ia-messages">
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto text-center space-y-6 mt-8">
              <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <Robot size={40} weight="fill" className="text-white"/>
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold">Bonjour, comment puis-je vous aider ?</h2>
                <p className="text-sm text-muted-foreground mt-2">Posez une question technique, demandez un script, un email, ou une explication.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-8">
                {suggestions.map((s, i) => (
                  <button key={i} data-testid={`ia-suggestion-${i}`} onClick={() => send(s.label)}
                    className="text-left p-3 rounded-lg border hover:bg-muted transition-colors text-sm flex items-start gap-2">
                    <span className="text-lg shrink-0">{s.emoji}</span>
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ia-msg-${i}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                  <Robot size={18} weight="fill" className="text-white"/>
                </div>
              )}
              <div className={`max-w-[80%] group ${m.role === "user" ? "" : "flex-1"}`}>
                <Card className={m.role === "user" ? "bg-primary text-primary-foreground border-primary" : ""}>
                  <CardContent className="p-3 md:p-4">
                    {m.role === "user" ? (
                      <p className="whitespace-pre-wrap text-sm">{m.content}</p>
                    ) : m.streaming ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-1" data-testid="ia-thinking">
                        <span className="inline-flex gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{animationDelay:"0ms"}}/>
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{animationDelay:"150ms"}}/>
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{animationDelay:"300ms"}}/>
                        </span>
                        <span>Assistant IA réfléchit… <span className="tabular-nums font-medium">{elapsed}s</span></span>
                      </div>
                    ) : (
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-2 prose-pre:my-2 prose-code:text-xs">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content || ""}</ReactMarkdown>
                      </div>
                    )}
                  </CardContent>
                </Card>
                {m.role === "assistant" && !m.streaming && m.content && (
                  <button onClick={() => copyMessage(m.content)}
                    className="mt-1 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
                    data-testid={`ia-copy-${i}`}>
                    <Copy size={12}/> Copier
                  </button>
                )}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <User size={16}/>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-3 md:p-4 border-t bg-background">
          <div className="max-w-4xl mx-auto flex gap-2 items-end">
            <Textarea ref={inputRef} data-testid="ia-input"
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Posez votre question… (Entrée pour envoyer · Shift+Entrée pour saut de ligne)"
              className="flex-1 min-h-[48px] max-h-[200px] resize-none" disabled={streaming}/>
            <Button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="ia-send-btn" className="h-12 px-4">
              <PaperPlaneTilt size={18} weight="fill"/>
            </Button>
          </div>
          <p className="text-[0.65rem] text-muted-foreground text-center mt-2">
            L&apos;IA peut se tromper. Vérifiez les informations sensibles avant utilisation.
          </p>
        </div>
      </main>
    </div>
  );
}
